var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { DefaultMCLRowFormatter, MultiColumnList, Select, AccordionSet, Accordion, Icon, Layout, } from '@folio/stripes/components';
import { RecordType } from '../types/api';
import SearchLayout from '../components/SearchLayout';
import PaneHelpButton from '../components/PaneHelpButton';
import HelpTooltip from '../components/HelpTooltip';
import ScreenHelp from '../components/ScreenHelp';
import { HEADINGS_HELP } from '../components/screenHelpContent';
import { TableLoadingSkeleton, TableErrorState, } from '../components/TableStates';
import useRecordIndex from '../hooks/useRecordIndex';
import { useIndexLanguages } from '../hooks/useIndexLanguages';
import useRecordDetail from '../hooks/useRecordDetail';
import RecordDetail from '../components/RecordDetail/RecordDetail';
import useRecordIndexGroups from '../hooks/useRecordIndexGroups';
import { OpenProposalBadges } from '../components/OpenProposalBadges';
import { recordDisplayId } from '../utils/recordDisplayId';
import { NO_ITEM_TO_VIEW } from '../utils/mclNoItemToView';
var isGroupRow = function (row) { return 'rowKind' in row; };
// CZNA-1513 — what a row tells assistive tech on top of MCL's own row props: a row of
// several records says whether it is unfolded, and the loading/error line under it takes
// no keyboard focus, because activating it does nothing.
export var registerRowProps = function (row, expanded) {
    if (isGroupRow(row))
        return row.rowKind === 'member' ? {} : { tabIndex: -1 };
    return row.recordCount > 1 ? { 'aria-expanded': expanded.includes(row.headingId) } : {};
};
// CZNA-1513 — without widths MCL sizes each column from a sample of its cells and stretches
// the LAST one over whatever is left, so the one-number count came out the widest and
// "Druh" (empty on every PREFERRED row) too narrow for "→ viz …". Shares of the register
// are what MCL gets until the register has been measured (and in jsdom, which never measures).
export var INDEX_COLUMN_WIDTHS = {
    headingValue: '38%',
    kind: '36%',
    language: '14%',
    recordCount: '12%',
};
var clamp = function (value, min, max) { return Math.min(max, Math.max(min, value)); };
// Shares alone fail the narrow register (detail pane open, ~340 px at 1280): 14 % broke
// "angličtina" mid-word and the count's header would not fit its 12 %, so it pushed the
// row past the register. Language and count therefore get a share held between their
// floor — "angličtina" (85 px with padding), the two-line count header — and the width
// that fits them on one line ("francouzština", 108 px); "Záhlaví" and "Druh" split the rest.
export var indexColumnWidths = function (registerWidth) {
    if (!registerWidth)
        return INDEX_COLUMN_WIDTHS;
    var language = Math.round(clamp(registerWidth * 0.16, 88, 124));
    var recordCount = Math.round(clamp(registerWidth * 0.14, 80, 120));
    var rest = Math.max(registerWidth - language - recordCount, 0);
    var headingValue = Math.floor(rest * 0.52);
    return {
        headingValue: "".concat(headingValue, "px"),
        kind: "".concat(rest - headingValue, "px"),
        language: "".concat(language, "px"),
        recordCount: "".concat(recordCount, "px"),
    };
};
// CZNA-1513 — see the constant; shared with the authority list since CZNA-1529.
export { NO_ITEM_TO_VIEW };
/** Room for the expand caret, so every heading starts at the same offset. */
var CARET_SLOT = { display: 'inline-block', width: '1.25rem' };
/** The records under an expanded row sit one caret slot further in. */
var MEMBER_INDENT = { display: 'block', paddingLeft: '2.5rem' };
var MUTED = { color: '#666' };
/** CZNA-1519 — the open-amendment marks follow the heading text after a small gap. */
var OPEN_PROPOSALS_GAP = { marginLeft: '6px' };
/**
 * CZNA-298 — standalone "Záhlaví" (authority headings register / rejstřík) page.
 *
 * <p>Extracted from {@code AuthoritiesList}'s former `?mode=index` branch into a
 * top-level nav item. The alphabetical register is keyset-paged and type-filtered
 * (CZNA-230); all that logic lives in {@link useRecordIndex} — this page only
 * renders it. The `?indexType=` URL param is owned by the hook and is preserved
 * across navigation.
 *
 * <p>Clicking a row opens the owning record's detail in the right pane (CZNA-334); a row
 * standing for several records expands in place to list them (CZNA-1513).
 */
/**
 * Distance (px) from the top of the scroll container within which an upward
 * auto-load is triggered. A small band (rather than scrollTop === 0) lets the
 * fetch start slightly before the user hits the very top, so the prepended
 * window is usually ready by the time they reach it.
 */
var UPWARD_LOAD_THRESHOLD_PX = 80;
var Headings = function (_a) {
    var _b, _c, _d, _f;
    var match = _a.match;
    var intl = useIntl();
    var history = useHistory();
    var location = useLocation();
    var index = useRecordIndex();
    var languages = useIndexLanguages(index.recordType).languages;
    // --- CZNA-315 Phase 2: scroll-driven UPWARD infinite load with scroll-anchoring ---
    //
    // Prepending rows to a virtualized stripes MCL makes MCL force `scrollTop = 0`
    // (its `CHANGE_DIFFERENCE` branch in MCLRenderer.componentDidUpdate), which would
    // jump the viewport to the top on every backward page — unusable. To keep the
    // same content under the viewport we:
    //   1. capture the scroll container's `scrollHeight` (h0) + `scrollTop` (t0) at
    //      the moment we ask for a backward window (`loadPrevious`), and
    //   2. after the prepend commits, restore `scrollTop = t0 + (newScrollHeight - h0)`
    //      in a `useLayoutEffect`. That effect runs AFTER MCL's own
    //      `componentDidUpdate` (children commit before parents), so it overwrites
    //      MCL's forced 0 before the browser paints — no flicker.
    // Live handle on MCL's actual scroll DOM node. Captured either from the onScroll
    // event's `target` (MCL gives us the native scroll event; `e.target` is its
    // `.mclScrollable` div) or — for the wheel/touch path, which must work BEFORE any
    // scroll event has fired — resolved from the wrapper ref via querySelector. MCL
    // exposes no ref to this node, so these are the only handles available.
    var scrollNodeRef = useRef(null);
    // Wrapper around MCL; the wheel/touch listeners live here so the upward-intent
    // detection works even at the very top, where the browser emits NO scroll event.
    var wrapperRef = useRef(null);
    // Width of the register, for sizing its columns (CZNA-1513). The wrapper comes and goes
    // with the loading skeleton, so it is observed from a callback ref rather than an effect.
    var _g = useState(), registerWidth = _g[0], setRegisterWidth = _g[1];
    var resizeObserverRef = useRef(null);
    var setWrapperNode = useCallback(function (node) {
        var _a;
        wrapperRef.current = node;
        (_a = resizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        resizeObserverRef.current = null;
        if (!node || typeof ResizeObserver === 'undefined')
            return;
        resizeObserverRef.current = new ResizeObserver(function (_a) {
            var entry = _a[0];
            setRegisterWidth(Math.floor(entry.contentRect.width));
        });
        resizeObserverRef.current.observe(node);
    }, []);
    var columnWidths = useMemo(function () { return indexColumnWidths(registerWidth); }, [registerWidth]);
    // Last touch Y, to derive scroll-up intent from touchmove on trackpads/touch.
    var lastTouchYRef = useRef(null);
    // Snapshot of { scrollTop, scrollHeight, headingId } taken right before a backward
    // load. When set, the layout effect below restores the scroll offset once the
    // prepended rows have grown the container.
    var pendingPrependRef = useRef(null);
    // Where the user last left the list, for putting it back after a row expands (CZNA-1513).
    var lastScrollTopRef = useRef(0);
    var entries = index.entries, hasMoreBefore = index.hasMoreBefore, isFetching = index.isFetching, loadPrevious = index.loadPrevious;
    var firstHeadingId = entries.length > 0 ? entries[0].headingId : undefined;
    // Resolve (and cache) MCL's `.mclScrollable` scroll node. The class is a CSS-module
    // hash, so we match by prefix. Needed because the wheel/touch path can fire before
    // any onScroll event has populated `scrollNodeRef`. A cached node is dropped once it
    // leaves the page: a jump or a filter change swaps MCL for the loading skeleton, and the
    // MCL that comes back scrolls a new node (CZNA-1513).
    var resolveScrollNode = useCallback(function () {
        var _a, _b;
        if (scrollNodeRef.current && scrollNodeRef.current.isConnected !== false)
            return scrollNodeRef.current;
        var node = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('[class*="mclScrollable"]')) !== null && _b !== void 0 ? _b : null;
        if (node)
            scrollNodeRef.current = node;
        return node;
    }, []);
    // Guarded trigger for an upward page. Snapshots the pre-prepend geometry, then
    // asks the hook for a backward window (which the merge effect prepends).
    var triggerLoadPrevious = useCallback(function () {
        if (!hasMoreBefore || isFetching)
            return;
        var node = resolveScrollNode();
        pendingPrependRef.current = {
            scrollTop: node ? node.scrollTop : 0,
            scrollHeight: node ? node.scrollHeight : 0,
            anchorHeadingId: firstHeadingId,
        };
        loadPrevious();
    }, [hasMoreBefore, isFetching, firstHeadingId, loadPrevious, resolveScrollNode]);
    // MCL onScroll handler: capture the scroll node and fire the upward auto-load when
    // the user scrolls into the top band. `e.target` is MCL's `.mclScrollable` div.
    // This path works mid-list (where scrolling up still emits scroll events) but CANNOT
    // fire when the window is anchored at the very top (scrollTop === 0): the browser
    // emits no scroll event because the container can't move further up. The wheel/touch
    // handlers below cover that top-boundary case.
    var handleScroll = useCallback(function (e) {
        var _a;
        var node = (_a = e === null || e === void 0 ? void 0 : e.target) !== null && _a !== void 0 ? _a : null;
        if (!node)
            return;
        scrollNodeRef.current = node;
        lastScrollTopRef.current = node.scrollTop;
        if (node.scrollTop <= UPWARD_LOAD_THRESHOLD_PX) {
            triggerLoadPrevious();
        }
    }, [triggerLoadPrevious]);
    // Wheel/touch upward-INTENT detection — the real top-boundary fix (CZNA-315 review).
    // Unlike `scroll`, `wheel` and `touchmove` events DO fire even when the element is
    // already at scrollTop 0 and can't move further up. When the user expresses upward
    // intent near the top, kick off `loadPrevious`; once the first backward window is
    // prepended and scrollTop is compensated to a positive value, the normal onScroll
    // path resumes for subsequent upward loads.
    var handleWheel = useCallback(function (e) {
        if (e.deltaY >= 0)
            return; // only upward intent
        var node = resolveScrollNode();
        if (node && node.scrollTop <= UPWARD_LOAD_THRESHOLD_PX) {
            triggerLoadPrevious();
        }
    }, [resolveScrollNode, triggerLoadPrevious]);
    var handleTouchStart = useCallback(function (e) {
        var _a, _b;
        lastTouchYRef.current = (_b = (_a = e.touches[0]) === null || _a === void 0 ? void 0 : _a.clientY) !== null && _b !== void 0 ? _b : null;
    }, []);
    var handleTouchMove = useCallback(function (e) {
        var _a, _b;
        var y = (_b = (_a = e.touches[0]) === null || _a === void 0 ? void 0 : _a.clientY) !== null && _b !== void 0 ? _b : null;
        var lastY = lastTouchYRef.current;
        lastTouchYRef.current = y;
        if (y === null || lastY === null)
            return;
        // Finger moving DOWN (y increasing) = content scrolls up = upward intent.
        if (y <= lastY)
            return;
        var node = resolveScrollNode();
        if (node && node.scrollTop <= UPWARD_LOAD_THRESHOLD_PX) {
            triggerLoadPrevious();
        }
    }, [resolveScrollNode, triggerLoadPrevious]);
    // After a backward page commits, restore the viewport so the same heading stays
    // under the user's eyes. Keyed on the first entry id + list length: a prepend
    // changes both. Runs after MCL's componentDidUpdate (which set scrollTop = 0).
    useLayoutEffect(function () {
        var pending = pendingPrependRef.current;
        if (!pending)
            return;
        var node = resolveScrollNode();
        // Only restore once the prepend has actually landed (first row id changed and
        // the container grew); otherwise wait for the real backward window to arrive.
        if (!node || firstHeadingId === pending.anchorHeadingId)
            return;
        var delta = node.scrollHeight - pending.scrollHeight;
        if (delta > 0) {
            node.scrollTop = pending.scrollTop + delta;
        }
        pendingPrependRef.current = null;
    }, [firstHeadingId, entries.length, resolveScrollNode]);
    // CZNA-341 — keep-off-the-floor guard for the scrollbar up-arrow (and keyboard).
    //
    // The wheel/touch handlers above cover the top boundary for those input devices,
    // but a click on the native scrollbar's up-arrow emits NO event of any kind when
    // the container is already at `scrollTop === 0`: there is nothing to scroll, and
    // the browser exposes no "tried to scroll a pinned scrollbar" event to hook. So
    // when the loaded window rests exactly at the top while earlier headings still
    // exist above (`hasMoreBefore`), the up-arrow does nothing — the reported bug
    // ("funguje jen drag": dragging the thumb shakes scrollTop off 0, an arrow click
    // can't).
    //
    // The fix is to never let the window rest at an un-scrollable top: hold `scrollTop`
    // at a single pixel of headroom. An up-arrow click (or ArrowUp/PageUp) then has a
    // pixel to travel, which fires a real `scroll` event that `handleScroll` turns into
    // `loadPrevious` like any other upward input — and the existing scroll-anchoring
    // effect keeps the viewport stable across the prepend. One pixel is invisible and,
    // because we only nudge while resting at 0, it never fights an in-flight prepend
    // (guarded by `pendingPrependRef`) or the anchoring restore above (which runs first
    // and leaves a positive scrollTop, so the `=== 0` test then fails).
    useLayoutEffect(function () {
        if (isFetching || !hasMoreBefore || pendingPrependRef.current)
            return;
        var node = resolveScrollNode();
        if (node && node.scrollTop === 0) {
            node.scrollTop = 1;
        }
    }, [entries.length, hasMoreBefore, isFetching, resolveScrollNode]);
    // CZNA-334 — the register has its own master-detail pane: clicking a heading opens
    // the owning authority's preview in the right pane (URL-driven via `/headings/:id`),
    // exactly like the authorities list — instead of jumping the user out into the
    // Autority section. The non-exact route mount already matches `/headings/:id`, so we
    // just read the `:id` here. For VARIANT rows `recordId` already points to the owning
    // PREFERRED record, so preferred and variant rows resolve to the same detail target.
    //
    var detailMatch = useRouteMatch("".concat(match.path, "/:id"));
    var selectedId = detailMatch === null || detailMatch === void 0 ? void 0 : detailMatch.params.id;
    // CZNA-1513 — a row standing for more than one record (homonyms grouped by CZNA-1078)
    // expands in place into those records, as Aleph's "short list" does, and each of them
    // opens in the detail pane like a row of one record. The list stays in the register, so
    // the right-hand side only ever holds the detail.
    var _h = useState([]), expanded = _h[0], setExpanded = _h[1];
    // A row's records are read under the filters in force; new filters, or a jump, mean a
    // new register (the accumulator starts empty), so nothing stays expanded or fetched.
    useEffect(function () {
        setExpanded(function (prev) { return (prev.length ? [] : prev); });
    }, [index.recordType, index.language, index.status]);
    useEffect(function () {
        if (entries.length === 0)
            setExpanded(function (prev) { return (prev.length ? [] : prev); });
    }, [entries.length]);
    var groups = useRecordIndexGroups(expanded, index.language, index.status);
    var rows = useMemo(function () { return entries.flatMap(function (entry) {
        if (!expanded.includes(entry.headingId))
            return [entry];
        var state = groups.get(entry.headingId);
        if (!state || state.isLoading)
            return [entry, { rowKind: 'loading', parent: entry }];
        if (state.isError || !state.group)
            return [entry, { rowKind: 'error', parent: entry }];
        return __spreadArray([entry], state.group.records.map(function (member) { return ({ rowKind: 'member', parent: entry, member: member }); }), true);
    }); }, [entries, expanded, groups]);
    var toggleGroup = useCallback(function (headingId) {
        var node = resolveScrollNode();
        if (node)
            lastScrollTopRef.current = node.scrollTop;
        setExpanded(function (prev) { return (prev.includes(headingId)
            ? prev.filter(function (id) { return id !== headingId; })
            : __spreadArray(__spreadArray([], prev, true), [headingId], false)); });
    }, [resolveScrollNode]);
    // Rows unfolding mid-list look to MCL like a re-sort, and it scrolls to the top
    // (MCLRenderer.componentDidUpdate, the same jump the prepend above works around). The
    // register itself did not move, so the viewport goes back where the user left it. A
    // change of `entries` is a page or a new register and has its own handling.
    //
    // Rows unfolding while a backward page is in flight grow the list below the viewport;
    // the prepend's anchoring counts every pixel of growth as prepended, so its snapshot
    // is taken again from here.
    var committedRef = useRef({ rows: rows, entries: entries });
    useLayoutEffect(function () {
        var previous = committedRef.current;
        committedRef.current = { rows: rows, entries: entries };
        if (previous.rows === rows || previous.entries !== entries)
            return;
        var node = resolveScrollNode();
        if (!node)
            return;
        node.scrollTop = lastScrollTopRef.current;
        if (pendingPrependRef.current) {
            pendingPrependRef.current = __assign(__assign({}, pendingPrependRef.current), { scrollTop: node.scrollTop, scrollHeight: node.scrollHeight });
        }
    }, [rows, entries, resolveScrollNode]);
    // The register's filters live in the query string (useRecordIndex), so every navigation
    // carries it: a push without it reads as "filters cleared" and resets the register.
    var navigate = useCallback(function (pathname) { return history.push({ pathname: pathname, search: location.search }); }, [history, location.search]);
    var openDetail = useCallback(function (recordId) { return navigate("".concat(match.path, "/").concat(recordId)); }, [navigate, match.path]);
    var openRow = useCallback(function (row) {
        if (row.recordCount > 1) {
            toggleGroup(row.headingId);
        }
        else {
            openDetail(row.recordId);
        }
    }, [toggleGroup, openDetail]);
    var closeDetail = useCallback(function () { return navigate(match.path); }, [navigate, match.path]);
    var rowFormatter = useCallback(function (row) { return DefaultMCLRowFormatter(__assign(__assign({}, row), { rowProps: __assign(__assign({}, row.rowProps), registerRowProps(row.rowData, expanded)) })); }, [expanded]);
    var handleRowClick = useCallback(function (_e, row) {
        if (!isGroupRow(row)) {
            openRow(row);
        }
        else if (row.rowKind === 'member') {
            openDetail(row.member.recordId);
        }
    }, [openRow, openDetail]);
    // CZNA-1513 — the record count is informative only, so it is the last column. What
    // "Druh" and the count carry is not readable from their values alone, so both headers
    // explain themselves, as the review queue's columns do.
    var indexColumnMapping = useMemo(function () {
        var columnHeader = function (column) { return (React.createElement("span", null,
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.index.column.".concat(column) }),
            React.createElement(HelpTooltip, { messageId: "ui-czna-authorities.index.column.".concat(column, ".help"), testKey: column, idPrefix: "headings-column-help" }))); };
        return {
            headingValue: React.createElement(FormattedMessage, { id: "ui-czna-authorities.index.column.heading" }),
            kind: columnHeader('kind'),
            language: React.createElement(FormattedMessage, { id: "ui-czna-authorities.index.column.language" }),
            recordCount: columnHeader('recordCount'),
        };
    }, []);
    var indexFormatter = useMemo(function () { return ({
        // CZNA-353 — PREFERRED headings are graphically highlighted (bold) directly in
        // the listing, not just via the textual "druh" ("kind") column. Bold text is the
        // simplest highlight that fits Stripes' existing conventions (see e.g. FieldRow's
        // `fontWeight: 700` for the "selected" state) without introducing a hardcoded
        // color or a new CSS module for a single-property style.
        //
        // CZNA-1513 — a row of several records carries a caret that shows whether it is
        // expanded; the records under it name their own preferred heading and ID.
        headingValue: function (row) {
            var _a, _b, _c;
            if (isGroupRow(row)) {
                if (row.rowKind !== 'member') {
                    return (React.createElement("span", { style: __assign(__assign({}, MEMBER_INDENT), MUTED) },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.index.group.".concat(row.rowKind) })));
                }
                var displayId = recordDisplayId(__assign(__assign({}, row.member), { type: row.parent.recordType }));
                return (React.createElement("span", { style: MEMBER_INDENT }, (_a = row.member.preferredHeadingValue) !== null && _a !== void 0 ? _a : '',
                    displayId && React.createElement("span", { style: MUTED }, " \u00B7 ".concat(displayId)),
                    ((_b = row.member.openProposals) === null || _b === void 0 ? void 0 : _b.length) ? (React.createElement("span", { style: OPEN_PROPOSALS_GAP },
                        React.createElement(OpenProposalBadges, { proposals: row.member.openProposals }))) : null));
            }
            return (React.createElement(React.Fragment, null,
                React.createElement("span", { style: CARET_SLOT }, row.recordCount > 1 && (React.createElement(Icon, { icon: expanded.includes(row.headingId) ? 'caret-down' : 'caret-right', size: "small" }))),
                React.createElement("span", { style: row.headingType === 'PREFERRED' ? { fontWeight: 700 } : undefined }, row.headingValue),
                ((_c = row.openProposals) === null || _c === void 0 ? void 0 : _c.length) ? (React.createElement("span", { style: OPEN_PROPOSALS_GAP },
                    React.createElement(OpenProposalBadges, { proposals: row.openProposals }))) : null));
        },
        recordCount: function (row) { return (isGroupRow(row) ? '' : row.recordCount); },
        // CZNA-1168 — the heading's language (from 4XX $2), so "Berlin" in English and
        // "Berlin" in German are told apart. CZNA-1513 — named from the language register;
        // the raw MARC code only where the register has no term for it.
        language: function (row) { var _a, _b; return (isGroupRow(row) ? '' : (_b = (_a = row.languageLabel) !== null && _a !== void 0 ? _a : row.language) !== null && _b !== void 0 ? _b : ''); },
        // CZNA-230 / CZNA-334 — VARIANT rows render "viz {preferred}" as a clickable
        // cross-reference. Clicking opens the owning (preferred) record's detail in the
        // right pane — the same target as a row click — instead of jumping within the
        // register (the former `jumpTo`, which reset the window to its start).
        // CZNA-1513 — a grouped row's "viz" names only one of its records' preferred forms,
        // so it expands the row like a click on it does; each record then shows its own.
        // Under an expanded row this column carries the record's first 678, which is what
        // tells homonyms apart.
        //
        // CZNA-408 — PREFERRED rows leave this column empty: the same "preferovaná" text
        // repeated on every PREFERRED row carried no information (it's already conveyed by
        // the bold heading value, CZNA-353); only VARIANT rows need this column, to link
        // back to their owning preferred form.
        kind: function (row) {
            var _a, _b;
            if (isGroupRow(row)) {
                var note = row.rowKind === 'member' ? row.member.note : null;
                return note ? (React.createElement("span", { title: note, style: __assign(__assign({}, MUTED), { display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, note)) : null;
            }
            var entry = row;
            return entry.headingType === 'VARIANT' ? (React.createElement("button", { type: "button", 
                // The full text where a narrow register (detail pane open) cuts it short.
                title: intl.formatMessage({ id: 'ui-czna-authorities.index.kind.variant' }, { preferred: (_a = entry.preferredHeadingValue) !== null && _a !== void 0 ? _a : '' }), onClick: function (e) {
                    e.stopPropagation();
                    openRow(entry);
                }, style: {
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: '#0052cc', textDecoration: 'underline', fontStyle: 'italic',
                } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.index.kind.variant", values: { preferred: (_b = entry.preferredHeadingValue) !== null && _b !== void 0 ? _b : '' } }))) : null;
        },
    }); }, [openRow, expanded, intl]);
    // CZNA-230 — type selector for the register: an "all types" option (untyped browse
    // across every authority type) plus one entry per RecordType.
    var indexTypeOptions = useMemo(function () { return __spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.index.type.all' }) }
    ], Object.values(RecordType).map(function (type) { return ({
        value: type,
        label: intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(type) }),
    }); }), true); }, [intl]);
    // CZNA-1168 — language selector: "all languages" plus what the corpus holds for the
    // selected type, most frequent first. An applied language the current type does not
    // carry stays selectable, so the Select never shows a value missing from its options.
    var indexLanguageOptions = useMemo(function () {
        var options = __spreadArray([
            { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.index.language.all' }) }
        ], languages.map(function (l) {
            var _a;
            return ({
                value: l.code,
                // CZNA-1513 — the language's name from the language register, else the code.
                label: "".concat((_a = l.label) !== null && _a !== void 0 ? _a : l.code, " (").concat(intl.formatNumber(l.headingCount), ")"),
            });
        }), true);
        if (index.language && !languages.some(function (l) { return l.code === index.language; })) {
            options.push({ value: index.language, label: index.language });
        }
        return options;
    }, [intl, languages, index.language]);
    // CZNA-1379 — the authority list's status values, so "Sloučené" means the same on both
    // screens. Active records by default: deleted and merged ones only when asked for.
    var indexStatusOptions = useMemo(function () { return ['active', 'merged', 'deleted', 'all']
        .map(function (value) { return ({
        value: value,
        label: intl.formatMessage({ id: "ui-czna-authorities.authorities.filter.status.".concat(value) }),
    }); }); }, [intl]);
    var indexFilterContent = (React.createElement(AccordionSet, null,
        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.index.type.label' }), separator: false },
            React.createElement(Select, { value: (_b = index.recordType) !== null && _b !== void 0 ? _b : '', dataOptions: indexTypeOptions, onChange: function (e) {
                    return index.setRecordType(e.target.value || undefined);
                } })),
        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.index.language.label' }), separator: false },
            React.createElement(Select, { "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.index.language.label' }), value: (_c = index.language) !== null && _c !== void 0 ? _c : '', dataOptions: indexLanguageOptions, onChange: function (e) {
                    return index.setLanguage(e.target.value || undefined);
                } })),
        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.index.status.label' }), separator: false },
            React.createElement(Select, { "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.index.status.label' }), value: index.status, dataOptions: indexStatusOptions, onChange: function (e) {
                    return index.setStatus(e.target.value);
                } }))));
    var renderIndexContent = function () {
        // CZNA-538 — before the empty branch, which used to absorb failures too.
        if (index.error && index.entries.length === 0) {
            return React.createElement(TableErrorState, { error: index.error, onRetry: index.refetch, testId: "index-error" });
        }
        // The register clears its accumulator on a filter change or a jump, so this branch
        // covers every wait that has nothing on screen; appends keep their rows and are
        // signalled by MCL's loader row at the end of the list instead (see below).
        if ((index.isLoading || index.isFetching) && index.entries.length === 0) {
            return React.createElement(TableLoadingSkeleton, { testId: "index-loading", columns: [10, 5, 2, 3], rows: 10 });
        }
        if (index.entries.length === 0 && !index.isLoading && !index.isFetching) {
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement("p", { style: { color: '#666' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.noResults" })))));
        }
        return (
        // Wrapper carries the wheel/touch upward-intent listeners (CZNA-315 review fix):
        // these fire at the top boundary where `scroll` events don't, and resolve MCL's
        // scroll node via querySelector so they don't depend on a prior scroll event.
        // height:100% so MCL's `autosize` still fills the pane.
        React.createElement("div", { ref: setWrapperNode, style: { height: '100%' }, onWheel: handleWheel, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, "data-testid": "headings-mcl-wrapper" },
            React.createElement(MultiColumnList, { autosize: true, virtualize: true, 
                // CZNA-538 — kept here, removed from the paged lists. This register appends:
                // the rows already read stay put and the wait is at the end of the list, which
                // is where MCL draws its loader row and where the user is already looking. An
                // overlay over the whole register would hide rows that are still current.
                loading: index.isFetching, contentData: rows, columnMapping: indexColumnMapping, columnWidths: columnWidths, visibleColumns: ['headingValue', 'kind', 'language', 'recordCount'], formatter: indexFormatter, nonInteractiveHeaders: ['headingValue', 'kind', 'language', 'recordCount'], onRowClick: handleRowClick, rowFormatter: rowFormatter, itemToView: NO_ITEM_TO_VIEW, 
                // CZNA-1513 — the record open in the detail, among the records of an expanded row.
                isSelected: function (_a) {
                    var item = _a.item;
                    return (isGroupRow(item) && item.rowKind === 'member' && item.member.recordId === selectedId);
                }, 
                // CZNA-315 Phase 1 — scroll-driven downward infinite load. With the default
                // `pagingType="scroll"` + `virtualize`, MCL renders a loader row once the
                // user scrolls to the end of the accumulated window, invoking this callback;
                // `loadMore()` advances the keyset cursor and appends the next page. Guard on
                // `hasMoreAfter` so we don't refetch the final window at the end of the
                // register (and on `isFetching` to avoid overlapping fetches in flight).
                onNeedMoreData: function () {
                    if (index.hasMoreAfter && !index.isFetching)
                        index.loadMore();
                }, 
                // CZNA-315 Phase 2 — scroll-driven UPWARD infinite load, mid-list. Tapping
                // MCL's raw scroll event fires `loadPrevious` near the top, then the layout
                // effect scroll-anchors the prepend. (The top-boundary case is handled by the
                // wrapper's wheel/touch listeners — see above.) Together these replace the
                // former "Načíst předchozí" button, making the register fully bidirectional.
                onScroll: handleScroll })));
    };
    // CZNA-334 — right-hand detail pane, identical to the authorities list
    // (AuthoritiesList): the owning record's preview, opened in place from the register.
    // RecordDetail already carries the "Editovat" / "Navrhnout změnu" CTA that
    // intentionally navigates into the edit interface (under the Autority section).
    var detailRecord = useRecordDetail(selectedId).record;
    var preferredHeading = (_d = detailRecord === null || detailRecord === void 0 ? void 0 : detailRecord.headings) === null || _d === void 0 ? void 0 : _d.find(function (h) { return h.headingType === 'PREFERRED'; });
    var paneDetailTitle = (preferredHeading === null || preferredHeading === void 0 ? void 0 : preferredHeading.headingValue)
        || intl.formatMessage({ id: 'ui-czna-authorities.authorities.detail.title' });
    // CZNA-1456 — the detail brings its own Pane, so its actions can sit in the header.
    var recordDetailPane = selectedId ? (React.createElement(RecordDetail, { id: selectedId, onClose: closeDetail, paneProps: {
            defaultWidth: '44%',
            paneTitle: paneDetailTitle,
            paneSub: detailRecord
                ? "".concat(intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(detailRecord.type) }), " \u00B7 ").concat((_f = recordDisplayId(detailRecord)) !== null && _f !== void 0 ? _f : '')
                : undefined,
        } })) : undefined;
    return (React.createElement(SearchLayout, { list: index, searchPlaceholder: intl.formatMessage({ id: 'ui-czna-authorities.index.jumpTo.placeholder' }), paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.index.title" }), lastMenu: (React.createElement(PaneHelpButton, null,
            React.createElement(ScreenHelp, { spec: HEADINGS_HELP }))), filterContent: indexFilterContent, detailPane: recordDetailPane }, renderIndexContent()));
};
export default Headings;
